import axios from "axios";
import Cookies from "js-cookie";
import JwtHelper from "@features/utils/JwtHelper";
interface IThenFunction {
  (res: any): void;
}

interface IThenSpreadFunction {
  (...res: any[]): void;
}

interface IErrorFunction {
  (error: any, next: any): void;
}

interface IRequestParams {
  [name: string]: string | number | null | undefined;
}

export interface ServerError {
  errors: string[];
}

class AxiosHelper {
  private static error: any;
  private token: string | undefined;

  constructor() {
    this.token = Cookies.get("jwt");
    axios.defaults.headers.common["Authorization"] = `Bearer ${this.token}`;
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (401 === error.response.status) {
          this.genericCatch(error);
        }
        return Promise.reject(error);
      }
    );
  }

  public get(
    url: string,
    cb: IThenFunction,
    er?: IErrorFunction
  ): Promise<any> {
    return axios
      .get(url)
      .then((res) => cb(res))
      .catch((error) => this.generalCatch(error, er));
  }

  public post<T = any>(
    url: string,
    data: T,
    cb: IThenFunction,
    er?: IErrorFunction
  ): Promise<any> {
    return axios
      .post(url, data)
      .then((res) => cb(res))
      .catch((error) => this.generalCatch(error, er));
  }

  public patch<T = any>(
    url: string,
    data: T,
    cb: IThenFunction,
    er?: IErrorFunction
  ): Promise<any> {
    return axios
      .patch(url, data)
      .then((res) => cb(res))
      .catch((error) => this.generalCatch(error, er));
  }

  public put<T = any>(
    url: string,
    data: T,
    cb: IThenFunction,
    er?: IErrorFunction
  ): void {
    axios
      .put(url, data)
      .then((res) => cb(res))
      .catch((error) => this.generalCatch(error, er));
  }

  public delete<T = object>(
    url: string,
    data: T,
    cb: IThenFunction,
    er?: IErrorFunction
  ): Promise<void> {
    return axios
      .delete(url, data)
      .then((res) => cb(res))
      .catch((error) => this.generalCatch(error, er));
  }

  public all<T>(
    fn: (T | Promise<T>)[],
    cb: IThenSpreadFunction,
    er?: IErrorFunction
  ): Promise<any> {
    return axios
      .all(fn)
      .then(axios.spread((...res) => cb(...res)))
      .catch((error) => this.generalCatch(error, er));
  }

  public catch(error: any) {
    this.catchFn(error);
  }

  public paramBuilder(params: IRequestParams) {
    return Object.keys(params)
      .reduce((paramString, param) => {
        return params[param] !== undefined
          ? paramString + `${param}=${params[param]}&`
          : paramString;
      }, "?")
      .slice(0, -1);
  }

  private generalCatch(error: any, er?: IErrorFunction) {
    AxiosHelper.error = error;
    return er ? er(error, this.next) : this.genericCatch(error);
  }

  private next(error: any = AxiosHelper.error) {
    this.genericCatch(error);
  }

  private genericCatch(error: any) {
    this.catchFn(error);
  }

  private catchFn(error: any) {
    const tokenIsExpired = JwtHelper.tokenExpired(this.token);
    if (error.response?.status === 401) {
      window.location.replace(
        `/users/sign_in?return_url=${window.location.pathname}${
          tokenIsExpired && "&expired=true"
        }`
      );
    } else if (
      error.response?.status === 424 &&
      error.request.responseURL.includes(
        "api/funds/current_investor/deposit_addresses"
      )
    ) {
      window.location.replace(`/error/Deposit%20Maintenance`);
    } else {
      window.location.replace(`/error/${error.response?.status}`);
    }
  }
}

const BttfAxios: AxiosHelper = new AxiosHelper();

export default BttfAxios;
